import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../useAuth'; // Adjust path if needed
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'; // Example icon
import DialogflowMessenger from '../components/DialogflowMessenger';

/**
 * Renders a user-friendly 404 Not Found page.
 * Provides navigation options back to safety.
 *
 * @component
 * @example
 * return <NotFound />
 */
const NotFound = () => {
    const { t } = useTranslation('NotFound'); // Using 'global' namespace, adjust if needed
    const { currentUser } = useAuth();

    useEffect(() => {
        // Set page title dynamically using translation
        document.title = t('notFound.pageTitle', 'Page Not Found - ProRecruit');
        // Cleanup function
        return () => {
            document.title = 'ProRecruit'; // Reset to default on unmount
        };
    }, [t]); // Rerun if translation function changes

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 text-center font-sans">
                  <DialogflowMessenger />
                  {/* Optional: Icon or Illustration */}
            <ExclamationTriangleIcon className="h-20 w-20 sm:h-24 sm:w-24 text-yellow-500 mb-6 animate-pulse" />

            <h1 className="text-6xl sm:text-7xl md:text-8xl font-extrabold text-indigo-600 tracking-tight">
                404
            </h1>
            <h2 className="mt-4 text-2xl sm:text-3xl font-semibold text-slate-900 font-bold tracking-tight">
                {/* Provide a default value in case translation key is missing */}
                {t('notFound.title', 'Page Not Found')}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md mx-auto">
                {t('notFound.message', "Oops! The page you're looking for doesn't seem to exist. It might have been moved, deleted, or perhaps you mistyped the URL.")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                    to={currentUser ? "/dashboard" : "/"} // Go to dashboard if logged in, else home
                    className="px-6 py-3 bg-indigo-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out w-full sm:w-auto"
                >
                    {currentUser ? t('notFound.goToDashboard', 'Go to Dashboard') : t('notFound.goToHome', 'Go to Homepage')}
                </Link>
                <Link
                    to="/support" // Link to your support page
                    className="px-6 py-3 bg-gray-200 text-slate-600 leading-relaxed text-base font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-150 ease-in-out w-full sm:w-auto"
                >
                    {t('notFound.contactSupport', 'Contact Support')}
                </Link>
            </div>
        </div>
    );
};

// PropTypes can be added if the component ever accepts props
// NotFound.propTypes = {};

export default NotFound;